import { useEffect, useMemo, useState } from 'react';

const DISMISS_STORAGE_KEY = 'qrmaps.pwa.install.dismissed.v1';

function getNavigatorStandalone() {
  if (typeof navigator === 'undefined') return false;
  return Boolean(navigator.standalone);
}

function detectIos() {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(userAgent) || (
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  );
}

function isStandaloneDisplayMode() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

function loadDismissedState() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch (_error) {
    return false;
  }
}

function persistDismissedState(value) {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(DISMISS_STORAGE_KEY);
    }
  } catch (_error) {
    // Ignore storage failures; the prompt can reappear next session.
  }
}

export function usePwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => loadDismissedState());
  const [isStandalone, setIsStandalone] = useState(() => (
    isStandaloneDisplayMode() || getNavigatorStandalone()
  ));

  const isIos = useMemo(() => detectIos(), []);

  useEffect(() => {
    const updateStandaloneState = () => {
      setIsStandalone(isStandaloneDisplayMode() || getNavigatorStandalone());
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      persistDismissedState(false);
      setDismissed(false);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      persistDismissedState(true);
      setDismissed(true);
      updateStandaloneState();
    };

    updateStandaloneState();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('focus', updateStandaloneState);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('focus', updateStandaloneState);
    };
  }, []);

  const canInstall = Boolean(deferredPrompt);
  const shouldShowPrompt = !isStandalone && !dismissed && (canInstall || isIos);

  const dismissPrompt = () => {
    persistDismissedState(true);
    setDismissed(true);
  };

  const resetDismissedPrompt = () => {
    persistDismissedState(false);
    setDismissed(false);
  };

  const promptInstall = async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };

    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result?.outcome === 'accepted') {
      persistDismissedState(true);
      setDismissed(true);
    }
    setDeferredPrompt(null);
    return result || { outcome: 'unknown' };
  };

  return {
    canInstall,
    dismissPrompt,
    isIos,
    isStandalone,
    promptInstall,
    resetDismissedPrompt,
    shouldShowPrompt,
  };
}
